/** Batch-local ordered-array access; casts/copies/comparisons are canonical php-compat. */
import { PhpArray, PhpFloat, floatval, fromJson, normalizeKey, type PhpKeyInput } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { portWritesBrand, type PortWithWrites, type PortWriteIntent } from '../../port-contract';
export const isArray = (v: unknown): v is object => v !== null && typeof v === 'object' && !(v instanceof PhpFloat);
export const array = (v: unknown): PhpArray => v instanceof PhpArray ? v : fromJson(v) as PhpArray;
export function at(v: unknown, ...keys: PhpKeyInput[]): unknown {
    for (const key of keys) {
        if (!isArray(v)) return null;
        v = v instanceof PhpArray ? v.get(key) : (v as Record<string, unknown>)[String(normalizeKey(key))];
    }
    return v ?? null;
}
export function put(v: unknown, keys: PhpKeyInput[], value: unknown): null {
    if (!isArray(v)) throw new TypeError('PHP array assignment requires an array');
    const key = keys[0]!;
    const set = (entry: unknown): void => {
        if (v instanceof PhpArray) v.set(key, entry);
        else (v as Record<string, unknown>)[String(normalizeKey(key))] = entry;
    };
    if (keys.length === 1) { set(value); return null; }
    let child = at(v, key);
    if (!isArray(child)) { child = new PhpArray(); set(child); }
    put(child, keys.slice(1), value);
    return null;
}
export function drop(v: unknown, ...keys: PhpKeyInput[]): null {
    const parent = keys.length === 1 ? v : at(v, ...keys.slice(0, -1));
    if (parent instanceof PhpArray) parent.delete(keys[keys.length - 1]!);
    else if (isArray(parent)) delete (parent as Record<string, unknown>)[String(normalizeKey(keys[keys.length - 1]!))];
    return null;
}
export const num = (v: unknown): number => Number(floatval(v));
export const f = (v: number): PhpFloat => new PhpFloat(v);
export const list = (v: unknown[]): PhpArray => new PhpArray(v.map((x, i) => [i, x]));
export const record = (v: Record<string, unknown>): PhpArray => new PhpArray(Object.entries(v));
export const values = (v: unknown): unknown[] => v instanceof PhpArray ? [...v.values()] : isArray(v) ? Object.values(v) : [];
export const entries = (v: unknown): [string | number | bigint, unknown][] => v instanceof PhpArray ? [...v] : isArray(v) ? Object.entries(v).map(([k, value]) => [normalizeKey(k), value]) : [];
/** Retain a dependency's persistence intents in the caller's ordered save plan. */
export function collectWrites(result: unknown, writes: PortWriteIntent[]): unknown {
    const wrapped = result as PortWithWrites<unknown> | null | undefined;
    if (wrapped?.[portWritesBrand] === true) {
        writes.push(...wrapped.writes);
        return wrapped.value;
    }
    return result;
}
/** Synchronous UTF-8 SHA family for PHP's byte-string hashes; no crypto/DOM I/O. */
export function sha(text: string, bits: 160 | 256): string {
    const bytes: number[] = [];
    for (const c of text) {
        const p = c.codePointAt(0)!;
        if (p < 128)
            bytes.push(p);
        else if (p < 2048)
            bytes.push(192 | (p >> 6), 128 | (p & 63));
        else if (p < 65536)
            bytes.push(224 | (p >> 12), 128 | ((p >> 6) & 63), 128 | (p & 63));
        else
            bytes.push(240 | (p >> 18), 128 | ((p >> 12) & 63), 128 | ((p >> 6) & 63), 128 | (p & 63));
    }
    const length = bytes.length * 8;
    bytes.push(128);
    while (bytes.length % 64 !== 56)
        bytes.push(0);
    for (let i = 7; i >= 0; i--)
        bytes.push(i >= 4 ? 0 : (length >>> (i * 8)) & 255);
    const h = bits === 160 ? [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0] : [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const k = [0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2];
    const rol = (n: number, s: number) => (n << s) | (n >>> (32 - s)), ror = (n: number, s: number) => (n >>> s) | (n << (32 - s));
    for (let base = 0; base < bytes.length; base += 64) {
        const w: number[] = [];
        for (let i = 0; i < 16; i++)
            w[i] = (bytes[base + i * 4]! << 24) | (bytes[base + i * 4 + 1]! << 16) | (bytes[base + i * 4 + 2]! << 8) | bytes[base + i * 4 + 3]!;
        const state = h.slice();
        if (bits === 160) {
            for (let i = 16; i < 80; i++)
                w[i] = rol(w[i - 3]! ^ w[i - 8]! ^ w[i - 14]! ^ w[i - 16]!, 1);
            for (let i = 0; i < 80; i++) {
                const [a, b, c, d, e] = state as [
                    number,
                    number,
                    number,
                    number,
                    number
                ], fn = i < 20 ? (b & c) | (~b & d) : i < 40 ? b ^ c ^ d : i < 60 ? (b & c) | (b & d) | (c & d) : b ^ c ^ d, constant = i < 20 ? 0x5a827999 : i < 40 ? 0x6ed9eba1 : i < 60 ? 0x8f1bbcdc : 0xca62c1d6;
                state.splice(0, 5, (rol(a, 5) + fn + e + constant + w[i]!) | 0, a, rol(b, 30), c, d);
            }
        }
        else {
            for (let i = 16; i < 64; i++) {
                const x = w[i - 15]!, y = w[i - 2]!;
                w[i] = (w[i - 16]! + (ror(x, 7) ^ ror(x, 18) ^ (x >>> 3)) + w[i - 7]! + (ror(y, 17) ^ ror(y, 19) ^ (y >>> 10))) | 0;
            }
            for (let i = 0; i < 64; i++) {
                const [a, b, c, d, e, g, j, q] = state as [
                    number,
                    number,
                    number,
                    number,
                    number,
                    number,
                    number,
                    number
                ], t1 = (q + (ror(e, 6) ^ ror(e, 11) ^ ror(e, 25)) + ((e & g) ^ (~e & j)) + k[i]! + w[i]!) | 0, t2 = ((ror(a, 2) ^ ror(a, 13) ^ ror(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
                state.splice(0, 8, (t1 + t2) | 0, a, b, c, (d + t1) | 0, e, g, j);
            }
        }
        for (let i = 0; i < h.length; i++)
            h[i] = (h[i]! + state[i]!) | 0;
    }
    return h.map(n => (n >>> 0).toString(16).padStart(8, '0')).join('');
}
export function byteBase64(bytes: number[]): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    let out = '';
    for (let i = 0; i < bytes.length; i += 3) {
        const a = bytes[i]! & 255, b = (bytes[i + 1] ?? 0) & 255, c = (bytes[i + 2] ?? 0) & 255;
        out += chars[a >> 2]! + chars[((a & 3) << 4) | (b >> 4)]! + (i + 1 < bytes.length ? chars[((b & 15) << 2) | (c >> 6)]! : '=') + (i + 2 < bytes.length ? chars[c & 63]! : '=');
    }
    return out;
}
/** Request-owned local statics; no state is shared by independent port modules or invocations. */
export function requestMemo(env: Pick<PortEnv, 'functionStatics'>, name: string, slot = 'memo'): Record<string, unknown> {
    const key = `${name}::$${slot}`;
    return new Proxy(Object.create(null) as Record<string, unknown>, {
        get: (_host, entry: string) => at(env.functionStatics[key], entry) ?? undefined,
        set: (_host, entry: string, value: unknown) => {
            const memo = env.functionStatics[key] ?? (env.functionStatics[key] = list([]));
            put(memo, [entry], value);
            return true;
        },
    });
}
/** MD5 over UTF-8 bytes, matching the request-local PHP memo fingerprint. */
export function md5(text: string): string {
    const encoded = unescape(encodeURIComponent(text)), bytes = Array.from(encoded, c => c.charCodeAt(0)), bits = bytes.length * 8;
    bytes.push(128);
    while (bytes.length % 64 !== 56)
        bytes.push(0);
    for (let i = 0; i < 8; i++)
        bytes.push(i < 4 ? (bits >>> (8 * i)) & 255 : 0);
    const shifts = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21], constants = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296));
    const h = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];
    for (let off = 0; off < bytes.length; off += 64) {
        const w = Array.from({ length: 16 }, (_, i) => bytes[off + i * 4]! | (bytes[off + i * 4 + 1]! << 8) | (bytes[off + i * 4 + 2]! << 16) | (bytes[off + i * 4 + 3]! << 24));
        let [a, b, c, d] = h as [
            number,
            number,
            number,
            number
        ];
        for (let i = 0; i < 64; i++) {
            const fn = i < 16 ? (b & c) | (~b & d) : i < 32 ? (d & b) | (~d & c) : i < 48 ? b ^ c ^ d : c ^ (b | ~d), index = i < 16 ? i : i < 32 ? (5 * i + 1) % 16 : i < 48 ? (3 * i + 5) % 16 : (7 * i) % 16, sum = (a + fn + constants[i]! + w[index]!) | 0, s = shifts[Math.floor(i / 16) * 4 + i % 4]!, next = (b + ((sum << s) | (sum >>> (32 - s)))) | 0;
            [a, b, c, d] = [d, next, b, c];
        }
        for (const [i, v] of [a, b, c, d].entries())
            h[i] = (h[i]! + v) | 0;
    }
    return h.map(n => [0, 8, 16, 24].map(s => ((n >>> s) & 255).toString(16).padStart(2, '0')).join('')).join('');
}
